import { useState } from 'react';
import toast from 'react-hot-toast';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { Button } from '@/components/ui/NewButton';
import MatterDropdown from './MatterDropdown';

interface NoAccessToCaseProps {
  matterExists?: boolean;
  matterGuid?: string;
  matterName?: string;
  onJoined?: () => Promise<unknown>;
}

const NoAccessToCase = ({
  matterExists = true,
  matterGuid,
  matterName,
  onJoined,
}: NoAccessToCaseProps) => {
  const { isAdmin } = useAdminCheck();
  const { user: sessionUser } = useAuth();
  const [isJoining, setIsJoining] = useState(false);

  const canJoinAsAdmin = isAdmin && matterExists && !!matterGuid;
  const email = sessionUser?.dbUser?.email || sessionUser?.email;

  const handleJoin = async () => {
    if (!matterGuid || !email) {
      toast.error('Unable to join this matter');
      return;
    }
    setIsJoining(true);
    try {
      await backendClient.post(`/matter/${matterGuid}/members`, {
        email,
        role: 'Member',
      });
      toast.success('Joined matter');
      await onJoined?.();
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to join matter');
    } finally {
      setIsJoining(false);
    }
  };

  const title = canJoinAsAdmin
    ? "You're not a member of this matter"
    : matterExists
      ? 'Access Denied'
      : 'Matter Not Found';

  const description = canJoinAsAdmin
    ? matterName
      ? `Join “${matterName}” to open it. As an admin you can add yourself as a member.`
      : 'Join this matter to open it. As an admin you can add yourself as a member.'
    : matterExists
      ? "You don't have permission to access this matter."
      : "The matter you're trying to access doesn't exist or you don't have access to it.";

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 px-4 py-12">
      <h2 className="text-2xl font-semibold text-crimson-500">{title}</h2>
      <p className="mb-2 max-w-md text-center text-gray-700">{description}</p>
      {canJoinAsAdmin ? (
        <Button
          variant="crimson"
          text="Join this matter"
          loading={isJoining}
          loadingText="Joining…"
          disabled={!email || isJoining}
          onClick={() => {
            void handleJoin();
          }}
        />
      ) : (
        <div className="flex flex-col items-center gap-4">
          <div className="w-64">
            <p className="mb-2 text-sm text-gray-500">Select a matter you have access to:</p>
            <MatterDropdown lightTrigger />
          </div>
        </div>
      )}
    </div>
  );
};

export default NoAccessToCase;
